/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { useActivityLogger, MusicalContext, InstrumentContext, ActivityEventType } from '../hooks/useActivityLogger';

interface ActivityLoggerContextValue {
  // Set current musical context (called when selection changes)
  setMusicalContext: (context: MusicalContext) => void;
  // Set current instrument context (called when instrument settings change)
  setInstrumentContext: (context: InstrumentContext) => void;
  // Log an event with current context automatically applied
  logEvent: (eventType: ActivityEventType, payload?: Record<string, unknown>) => void;
  // Convenience methods that include current context
  logScaleChange: (from: string, to: string) => void;
  logChordChange: (from: string, to: string) => void;
  logKeyChange: (from: string, to: string) => void;
  logModeToggle: (from: 'scale' | 'chord', to: 'scale' | 'chord') => void;
  logFretboardClick: (stringIndex: number, fret: number, note: string) => void;
  logDiatonicChord: (degree: number, chord: string, romanNumeral: string) => void;
  logFingeringView: (chord: string, fingeringIndex: number) => void;
  logJamTrackStart: (trackId: string, style: string, key: string, bpm: number) => void;
  logJamTrackStop: (trackId: string, playDurationSeconds: number) => void;
}

const ActivityLoggerContext = createContext<ActivityLoggerContextValue | null>(null);

interface ActivityLoggerProviderProps {
  children: ReactNode;
}

export const ActivityLoggerProvider: React.FC<ActivityLoggerProviderProps> = ({ children }) => {
  const logger = useActivityLogger();

  // Store current contexts in refs to avoid stale closures
  const musicalContextRef = React.useRef<MusicalContext | undefined>(undefined);
  const instrumentContextRef = React.useRef<InstrumentContext | undefined>(undefined);

  const setMusicalContext = useCallback((context: MusicalContext) => {
    musicalContextRef.current = context;
  }, []);

  const setInstrumentContext = useCallback((context: InstrumentContext) => {
    instrumentContextRef.current = context;
  }, []);

  const logEvent = useCallback((eventType: ActivityEventType, payload?: Record<string, unknown>) => {
    logger.logActivity({
      eventType,
      musicalContext: musicalContextRef.current,
      instrumentContext: instrumentContextRef.current,
      payload,
    });
  }, [logger]);

  const logScaleChange = useCallback((from: string, to: string) => {
    logger.logScaleSelected(from, to, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logChordChange = useCallback((from: string, to: string) => {
    logger.logChordSelected(from, to, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logKeyChange = useCallback((from: string, to: string) => {
    logger.logKeyChanged(from, to, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logModeToggle = useCallback((from: 'scale' | 'chord', to: 'scale' | 'chord') => {
    logger.logModeToggled(from, to, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logFretboardClick = useCallback((stringIndex: number, fret: number, note: string) => {
    logger.logFretboardNoteClicked(stringIndex, fret, note, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logDiatonicChord = useCallback((degree: number, chord: string, romanNumeral: string) => {
    logger.logDiatonicChordSelected(degree, chord, romanNumeral, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logFingeringView = useCallback((chord: string, fingeringIndex: number) => {
    logger.logFingeringViewed(chord, fingeringIndex, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logJamTrackStart = useCallback((trackId: string, style: string, key: string, bpm: number) => {
    logger.logJamTrackStarted(trackId, style, key, bpm, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const logJamTrackStop = useCallback((trackId: string, playDurationSeconds: number) => {
    logger.logJamTrackStopped(trackId, playDurationSeconds, musicalContextRef.current, instrumentContextRef.current);
  }, [logger]);

  const value = useMemo(() => ({
    setMusicalContext,
    setInstrumentContext,
    logEvent,
    logScaleChange,
    logChordChange,
    logKeyChange,
    logModeToggle,
    logFretboardClick,
    logDiatonicChord,
    logFingeringView,
    logJamTrackStart,
    logJamTrackStop,
  }), [
    setMusicalContext,
    setInstrumentContext,
    logEvent,
    logScaleChange,
    logChordChange,
    logKeyChange,
    logModeToggle,
    logFretboardClick,
    logDiatonicChord,
    logFingeringView,
    logJamTrackStart,
    logJamTrackStop,
  ]);

  return (
    <ActivityLoggerContext.Provider value={value}>
      {children}
    </ActivityLoggerContext.Provider>
  );
};

export const useActivityLoggerContext = (): ActivityLoggerContextValue => {
  const context = useContext(ActivityLoggerContext);
  if (!context) {
    throw new Error('useActivityLoggerContext must be used within an ActivityLoggerProvider');
  }
  return context;
};

export default ActivityLoggerContext;
